import { API, Auth } from 'aws-amplify';
import { BENCHMARK_CODE_MAP, REPORTING_LEVEL_GROUPINGS } from '../Constants.js';
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { getAuthToken } from '../utils/AuthUtils';
import './styles.scss';
import { Card, Form, Button, Alert } from 'react-bootstrap';
import Chart from 'chart.js/auto';

const surveyYear = 2026;
const previousSurveyYear = surveyYear - 1;
const CURSOR_HANDLE_RADIUS = 10;
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const formatChartValue = (value) => Math.round(value).toLocaleString();
const formatRSquared = (value) => Number(value).toFixed(3);
const formatScopeValue = (value) => Math.round(value).toLocaleString();
const parseScopeValue = (value) => Number(String(value).replaceAll(',', ''));
const SCOPE_CONFIG = Object.freeze({
    revenue: Object.freeze({ minimum: 1, maximum: 20000, selected: 10000, upperLimit: 250000 }),
    marketCap: Object.freeze({ minimum: 1, maximum: 100000, selected: 10000, upperLimit: 3500000 }),
});
const getScopeConfig = (regressionType) => regressionType === 'revenue' ? SCOPE_CONFIG.revenue : SCOPE_CONFIG.marketCap;
const scopeCursorPlugin = {
    id: 'scopeCursorPlugin',
    afterDatasetsDraw(chart, args, pluginOptions) {
        const xScale = chart.scales.x;
        const yScale = chart.scales.y;
        if (!xScale || !yScale || !pluginOptions?.xValue) {
            return;
        }

        const cursorX = xScale.getPixelForValue(pluginOptions.xValue);
        const topY = yScale.top;
        const bottomY = yScale.bottom;
        const { ctx } = chart;

        ctx.save();
        ctx.strokeStyle = '#0f172a';
        ctx.lineWidth = 2;
        ctx.setLineDash([6, 4]);
        ctx.beginPath();
        ctx.moveTo(cursorX, topY);
        ctx.lineTo(cursorX, bottomY);
        ctx.stroke();
        ctx.setLineDash([]);

        const cursorValues = (pluginOptions.cursorValues ?? []).filter((entry) => typeof entry.value === 'number');
        const sortedCursorValues = [...cursorValues].sort((a, b) => b.value - a.value);

        cursorValues.forEach((entry) => {
            if (typeof entry.value !== 'number') {
                return;
            }

            const pointY = yScale.getPixelForValue(entry.value);
            ctx.fillStyle = entry.color;
            ctx.beginPath();
            ctx.arc(cursorX, pointY, 4, 0, Math.PI * 2);
            ctx.fill();

            const label = `${entry.label}: ${formatChartValue(entry.value)}`;
            ctx.font = '600 21px sans-serif';
            const labelWidth = ctx.measureText(label).width;
            const boxWidth = labelWidth + 18;
            const boxHeight = 28;
            const boxX = clamp(cursorX + 12, xScale.left, xScale.right - boxWidth);
            const isSingleCurve = sortedCursorValues.length === 1;
            const isLargestValue = !isSingleCurve && sortedCursorValues[0]?.label === entry.label && sortedCursorValues[0]?.value === entry.value;
            const preferredY = isSingleCurve
                ? pointY + 14
                : isLargestValue
                    ? pointY - boxHeight - 14
                    : pointY + 14;
            const boxY = clamp(preferredY, topY + 6, bottomY - boxHeight - 6);

            ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
            ctx.strokeStyle = entry.color;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.roundRect(boxX, boxY, boxWidth, boxHeight, 6);
            ctx.fill();
            ctx.stroke();

            ctx.fillStyle = '#0f172a';
            ctx.textBaseline = 'middle';
            ctx.fillText(label, boxX + 9, boxY + (boxHeight / 2));
        });

        ctx.fillStyle = '#0f172a';
        ctx.beginPath();
        ctx.arc(cursorX, bottomY, 5, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
    }
};

const legendSpacingPlugin = {
    id: 'legendSpacingPlugin',
    beforeInit(chart) {
        if (!chart.legend) {
            return;
        }
        const originalFit = chart.legend.fit.bind(chart.legend);
        chart.legend.fit = function fitLegendWithSpacing() {
            originalFit();
            this.height += 14;
        };
    }
};

const curveEndLabelPlugin = {
    id: 'curveEndLabelPlugin',
    afterDatasetsDraw(chart) {
        const xScale = chart.scales.x;
        const yScale = chart.scales.y;
        if (!xScale || !yScale) {
            return;
        }

        const { ctx } = chart;
        const boxHeight = 28;
        const boxGap = 6;
        const labels = chart.data.datasets.flatMap((dataset, datasetIndex) => {
            const rSquared = Number(dataset.rSquared);
            const meta = chart.getDatasetMeta(datasetIndex);
            const endPoint = meta.data[meta.data.length - 1];
            if (dataset.rSquared === null || dataset.rSquared === undefined || !Number.isFinite(rSquared) || meta.hidden || !endPoint) {
                return [];
            }

            const label = `${dataset.rSquaredLabel ?? 'R²'}: ${formatRSquared(rSquared)}`;
            ctx.save();
            ctx.font = '600 14px sans-serif';
            const boxWidth = ctx.measureText(label).width + 18;
            ctx.restore();

            return [{
                label,
                color: dataset.borderColor,
                pointX: endPoint.x,
                pointY: endPoint.y,
                boxWidth,
                boxY: clamp(endPoint.y - (boxHeight / 2), yScale.top + 6, yScale.bottom - boxHeight - 6)
            }];
        }).sort((left, right) => left.pointY - right.pointY);

        labels.forEach((entry, index) => {
            if (index === 0) {
                return;
            }
            const previousLabel = labels[index - 1];
            entry.boxY = Math.max(entry.boxY, previousLabel.boxY + boxHeight + boxGap);
        });

        for (let index = labels.length - 1; index >= 0; index -= 1) {
            const maximumY = index === labels.length - 1
                ? yScale.bottom - boxHeight - 6
                : labels[index + 1].boxY - boxHeight - boxGap;
            labels[index].boxY = Math.min(labels[index].boxY, maximumY);
        }

        ctx.save();
        ctx.font = '600 14px sans-serif';
        ctx.textBaseline = 'middle';
        labels.forEach((entry) => {
            const boxX = clamp(entry.pointX + 12, xScale.right + 6, chart.width - entry.boxWidth - 6);

            ctx.strokeStyle = entry.color;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(entry.pointX + 3, entry.pointY);
            ctx.lineTo(boxX, entry.boxY + (boxHeight / 2));
            ctx.stroke();

            ctx.fillStyle = entry.color;
            ctx.beginPath();
            ctx.arc(entry.pointX, entry.pointY, 4, 0, Math.PI * 2);
            ctx.fill();

            ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
            ctx.strokeStyle = entry.color;
            ctx.beginPath();
            ctx.roundRect(boxX, entry.boxY, entry.boxWidth, boxHeight, 6);
            ctx.fill();
            ctx.stroke();

            ctx.fillStyle = '#0f172a';
            ctx.fillText(entry.label, boxX + 9, entry.boxY + (boxHeight / 2));
        });
        ctx.restore();
    }
};

const MARKET_CAP_INDUSTRY_GROUPINGS = Object.freeze({
    GENERAL_INDUSTRY: "General Industry",
    UTILITIES: "Utilities",
    TECHNOLOGY: "Technology"
})


const REVENUE_INDUSTRY_GROUPINGS = Object.freeze({
    GENERAL_INDUSTRY: "General Industry",
    UTILITIES: "Utilities",
    RESTAURANT_HOSPITALITY: "Restaurants & Hospitality",
    CONSUMER_PACKAGED_GOODS: "Consumer Packaged Goods"
})

const SurveyRegressionDashboardConsole = ({ regressionType }) => {
    const initialScopeConfig = getScopeConfig(regressionType);
    const [selectedBenchmark, setSelectedBenchmark] = useState("Chief Executive Officer");
    const [selectedIndustryGrouping, setSelectedIndustryGrouping] = useState("General Industry");
    const [selectedReportingLevelGrouping, setSelectedReportingLevelGrouping] = useState("All Reporting Levels")
    const [currentYearData, setCurrentYearData] = useState({});
    const [previousYearData, setPreviousYearData] = useState({});
    const [selectedScope, setSelectedScope] = useState(initialScopeConfig.selected);
    const [scopeInputValue, setScopeInputValue] = useState(formatScopeValue(initialScopeConfig.selected));
    const [selectedChart, setSelectedChart] = useState("tdc");
    const [selectedPercentile, setSelectedPercentile] = useState("median");
    const [minScope, setMinScope] = useState(initialScopeConfig.minimum);
    const [maxScope, setMaxScope] = useState(initialScopeConfig.maximum);
    const [scopeUpperLimit, setScopeUpperLimit] = useState(initialScopeConfig.upperLimit);
    const [minScopeInputValue, setMinScopeInputValue] = useState(formatScopeValue(initialScopeConfig.minimum));
    const [maxScopeInputValue, setMaxScopeInputValue] = useState(formatScopeValue(initialScopeConfig.maximum));
    const [minScopeError, setMinScopeError] = useState("");
    const [maxScopeError, setMaxScopeError] = useState("");
    const [error, setError] = useState(null);
    const [displayError, setDisplayError] = useState(false);
    const [copyChartStatus, setCopyChartStatus] = useState('idle');
    const salaryRegressionChartCanvasRef = useRef(null);
    const salaryRegressionChartInstanceRef = useRef(null);
    const tacRegressionChartCanvasRef = useRef(null);
    const tacRegressionChartInstanceRef = useRef(null);
    const tdcRegressionChartCanvasRef = useRef(null);
    const tdcRegressionChartInstanceRef = useRef(null);
    const cursorDragStateRef = useRef({ dragging: false });

    const updateSelectedScope = useCallback((value) => {
        if (typeof value !== 'number' || Number.isNaN(value)) {
            return;
        }

        setSelectedScope(Math.round(clamp(value, minScope, maxScope)));
    }, [maxScope, minScope]);

    const commitScopeInputValue = useCallback(() => {
        const parsedValue = parseScopeValue(scopeInputValue);
        if (scopeInputValue === '' || Number.isNaN(parsedValue)) {
            setScopeInputValue(formatScopeValue(selectedScope));
            return;
        }

        updateSelectedScope(parsedValue);
    }, [scopeInputValue, selectedScope, updateSelectedScope]);

    const validateScopeBound = useCallback((value, allowZero = false) => {
        if (!Number.isInteger(value) || value < 0 || (!allowZero && value === 0)) {
            return allowZero
                ? "Enter an integer greater than or equal to zero."
                : "Enter an integer greater than zero.";
        }
        if (value > scopeUpperLimit) {
            return `Enter a value less than or equal to ${formatScopeValue(scopeUpperLimit)}.`;
        }
        return "";
    }, [scopeUpperLimit]);

    const commitMinScopeInputValue = useCallback(() => {
        if (minScopeInputValue.trim() === "") {
            setMinScopeError("Enter an integer greater than or equal to zero.");
            return;
        }
        const parsedValue = parseScopeValue(minScopeInputValue);
        const validationError = validateScopeBound(parsedValue, true);
        if (validationError) {
            setMinScopeError(validationError);
            return;
        }
        if (parsedValue >= maxScope) {
            setMinScopeError("Minimum scope must be less than maximum scope.");
            return;
        }

        setMinScope(parsedValue);
        setMinScopeInputValue(formatScopeValue(parsedValue));
        setMinScopeError("");
    }, [maxScope, minScopeInputValue, validateScopeBound]);

    const commitMaxScopeInputValue = useCallback(() => {
        const parsedValue = parseScopeValue(maxScopeInputValue);
        const validationError = validateScopeBound(parsedValue);
        if (validationError) {
            setMaxScopeError(validationError);
            return;
        }
        if (parsedValue <= minScope) {
            setMaxScopeError("Maximum scope must be greater than minimum scope.");
            return;
        }

        setMaxScope(parsedValue);
        setMaxScopeInputValue(formatScopeValue(parsedValue));
        setMaxScopeError("");
    }, [maxScopeInputValue, minScope, validateScopeBound]);

    const handleSetData = useCallback((data) => {
        if (data && (data.length === 2 || data.length === 1)) {
            if (data[0].year === surveyYear) {
                setCurrentYearData(data[0]);
                if (data.length === 2 && data[1].year === previousSurveyYear) {
                    setPreviousYearData(data[1]);
                }
            } else if (data[0].year === previousSurveyYear) {
                setPreviousYearData(data[0]);
                if (data.length === 2 && data[1].year === surveyYear) {
                    setCurrentYearData(data[1]);
                }
            } else {
                console.error('Unexpected year data received:', data);
            }

        }
    }, [])

    const calculatePredictedValues = useCallback((data) => {

        if (!data || !data.baseSalarySlope || selectedScope <= 0) {
            return {}
        }

        const salaryMedian = Math.exp(data.baseSalarySlope * Math.log(selectedScope) + data.baseSalaryIntercept) ?? undefined;
        const salary25th = salaryMedian * Math.exp(-0.675 * data.baseSalaryStdError) ?? undefined;
        const salary75th = salaryMedian * Math.exp(0.675 * data.baseSalaryStdError) ?? undefined;

        const tacMedian = Math.exp(data.tacSlope * Math.log(selectedScope) + data.tacIntercept) ?? undefined;
        const tac25th = tacMedian * Math.exp(-0.675 * data.tacStdError) ?? undefined;
        const tac75th = tacMedian * Math.exp(0.675 * data.tacStdError) ?? undefined;

        const tdcMedian = Math.exp(data.tdcSlope * Math.log(selectedScope) + data.tdcIntercept) ?? undefined;
        const tdc25th = tdcMedian * Math.exp(-0.675 * data.tdcStdError) ?? undefined;
        const tdc75th = tdcMedian * Math.exp(0.675 * data.tdcStdError) ?? undefined;

        return { salaryMedian, salary25th, salary75th, tacMedian, tac25th, tac75th, tdcMedian, tdc25th, tdc75th }
    }, [selectedScope])

    const currentYearPredictedValues = useMemo(() => calculatePredictedValues(currentYearData), [calculatePredictedValues, currentYearData]);
    const previousYearPredictedValues = useMemo(() => calculatePredictedValues(previousYearData), [calculatePredictedValues, previousYearData]);

    const adjustForSelectedPercentile = useCallback((medianValue, standardError) => {
        if (typeof medianValue !== 'number' || !Number.isFinite(medianValue)) {
            return null;
        }
        if (selectedPercentile === "median") {
            return medianValue;
        }

        const numericStandardError = Number(standardError);
        if (!Number.isFinite(numericStandardError)) {
            return null;
        }

        const percentileDirection = selectedPercentile === "75th" ? 1 : -1;
        return medianValue * Math.exp(percentileDirection * 0.675 * numericStandardError);
    }, [selectedPercentile]);

    const selectedPercentileKey = selectedPercentile === "75th"
        ? "75th"
        : selectedPercentile === "25th" ? "25th" : "Median";
    const currentYearSelectedPredictedValues = {
        salary: currentYearPredictedValues[`salary${selectedPercentileKey}`],
        tac: currentYearPredictedValues[`tac${selectedPercentileKey}`],
        tdc: currentYearPredictedValues[`tdc${selectedPercentileKey}`]
    };
    const previousYearSelectedPredictedValues = {
        salary: previousYearPredictedValues[`salary${selectedPercentileKey}`],
        tac: previousYearPredictedValues[`tac${selectedPercentileKey}`],
        tdc: previousYearPredictedValues[`tdc${selectedPercentileKey}`]
    };

    const regressionChartPoints = useMemo(() => {
        if (minScope < 0 || !maxScope || minScope >= maxScope) {
            return [];
        }

        const pointCount = Math.min(1000, Math.max(250, Math.ceil(maxScope - minScope) + 1));
        const sampledPoints = Array.from({ length: pointCount }, (_, index) => {
            const ratio = index / (pointCount - 1);
            return minScope + ((maxScope - minScope) * ratio);
        });

        const pointsWithCursor = [...sampledPoints, selectedScope];
        const roundedPoints = pointsWithCursor.map((value) => Number(value.toFixed(6)));

        return [...new Set(roundedPoints)].sort((a, b) => a - b);
    }, [maxScope, minScope, selectedScope]);

    const buildSalaryRegressionSeries = useCallback((data) => {
        if (!data?.baseSalarySlope || !data?.baseSalaryIntercept || regressionChartPoints.length === 0) {
            return [];
        }

        return regressionChartPoints.map((scopeValue) => ({
            x: scopeValue,
            y: scopeValue > 0
                ? adjustForSelectedPercentile(
                    Math.exp(data.baseSalarySlope * Math.log(scopeValue) + data.baseSalaryIntercept),
                    data.baseSalaryStdError
                )
                : null
        }));
    }, [adjustForSelectedPercentile, regressionChartPoints]);

    const buildTacRegressionSeries = useCallback((data) => {
        if (!data?.tacSlope || !data?.tacIntercept || regressionChartPoints.length === 0) {
            return [];
        }

        return regressionChartPoints.map((scopeValue) => ({
            x: scopeValue,
            y: scopeValue > 0
                ? adjustForSelectedPercentile(
                    Math.exp(data.tacSlope * Math.log(scopeValue) + data.tacIntercept),
                    data.tacStdError
                )
                : null
        }));
    }, [adjustForSelectedPercentile, regressionChartPoints]);

    const buildTdcRegressionSeries = useCallback((data) => {
        if (!data?.tdcSlope || !data?.tdcIntercept || regressionChartPoints.length === 0) {
            return [];
        }

        return regressionChartPoints.map((scopeValue) => ({
            x: scopeValue,
            y: scopeValue > 0
                ? adjustForSelectedPercentile(
                    Math.exp(data.tdcSlope * Math.log(scopeValue) + data.tdcIntercept),
                    data.tdcStdError
                )
                : null
        }));
    }, [adjustForSelectedPercentile, regressionChartPoints]);


    const currentYearSalaryRegressionSeries = useMemo(
        () => buildSalaryRegressionSeries(currentYearData),
        [buildSalaryRegressionSeries, currentYearData]
    );

    const currentYearTacRegressionSeries = useMemo(
        () => buildTacRegressionSeries(currentYearData),
        [buildTacRegressionSeries, currentYearData]
    );

    const currentYearTdcRegressionSeries = useMemo(
        () => buildTdcRegressionSeries(currentYearData),
        [buildTdcRegressionSeries, currentYearData]
    );

    const previousYearSalaryRegressionSeries = useMemo(
        () => buildSalaryRegressionSeries(previousYearData),
        [buildSalaryRegressionSeries, previousYearData]
    );

    const previousYearTacRegressionSeries = useMemo(
        () => buildTacRegressionSeries(previousYearData),
        [buildTacRegressionSeries, previousYearData]
    );

    const previousYearTdcRegressionSeries = useMemo(
        () => buildTdcRegressionSeries(previousYearData),
        [buildTdcRegressionSeries, previousYearData]
    );

    const getYoYChangePercent = (current, previous) => {
        if (typeof current !== 'number' || typeof previous !== 'number' || previous === 0) {
            return null;
        }

        return ((current - previous) / previous) * 100;
    };

    const formatYoYChange = (current, previous) => {
        const percentageChange = getYoYChangePercent(current, previous);

        if (percentageChange === null) {
            return 'N/A';
        }

        if (percentageChange < 0) {
            return `(${Math.abs(percentageChange).toFixed(1)}%)`;
        }

        return `${percentageChange.toFixed(1)}%`;
    };

    const formatNumber = (value) => {
        if (value === null || value === undefined) {
            return '';
        }
        if (typeof value === 'number') {
            return Math.round(value);;
        } else {
            return value;
        }
    }

    const isNegativeValue = (value) => {
        if (typeof value === 'number') {
            return value < 0;
        }

        return typeof value === 'string' && value.includes('(');
    }

    const calculateSampleChange = (currentYearValue, previousYearValue) => {
        if (isNaN(Number(currentYearValue)) || isNaN(Number(previousYearValue))) {
            return 'N/A';
        }
        return currentYearValue - previousYearValue;
    }

    const renderTableCell = (value) => (
        <td className={isNegativeValue(value) ? 'negative-value' : ''}>
            {value === null || value === undefined ? '' : value.toLocaleString()}
        </td>
    );

    const dataTable = (
        <table className="regression-data-table">
            <thead>
                <tr>
                    <th></th>
                    <th colSpan={3}>Base Salary</th>
                    <th colSpan={3}>Total Annual Compensation</th>
                    <th colSpan={3}>Total Direct Compensation</th>
                </tr>
            </thead>
            <thead>
                <tr>
                    <th>Predicted Values</th>
                    <th>{previousSurveyYear}</th>
                    <th>{surveyYear}</th>
                    <th>YoY Change</th>
                    <th>{previousSurveyYear}</th>
                    <th>{surveyYear}</th>
                    <th>YoY Change</th>
                    <th>{previousSurveyYear}</th>
                    <th>{surveyYear}</th>
                    <th>YoY Change</th>

                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>Sample Size</td>
                    {renderTableCell(previousYearData.baseSalaryDataPoints)}
                    {renderTableCell(currentYearData.baseSalaryDataPoints)}
                    {renderTableCell(calculateSampleChange(currentYearData.baseSalaryDataPoints, previousYearData.baseSalaryDataPoints))}
                    {renderTableCell(previousYearData.tacDataPoints)}
                    {renderTableCell(currentYearData.tacDataPoints)}
                    {renderTableCell(calculateSampleChange(currentYearData.tacDataPoints, previousYearData.tacDataPoints))}
                    {renderTableCell(previousYearData.tdcDataPoints)}
                    {renderTableCell(currentYearData.tdcDataPoints)}
                    {renderTableCell(calculateSampleChange(currentYearData.tdcDataPoints, previousYearData.tdcDataPoints))}
                </tr>
                <tr>
                    <td>75th Percentile</td>
                    {renderTableCell(formatNumber(previousYearPredictedValues.salary75th))}
                    {renderTableCell(formatNumber(currentYearPredictedValues.salary75th))}
                    {renderTableCell(formatYoYChange(currentYearPredictedValues.salary75th, previousYearPredictedValues.salary75th))}
                    {renderTableCell(formatNumber(previousYearPredictedValues.tac75th))}
                    {renderTableCell(formatNumber(currentYearPredictedValues.tac75th))}
                    {renderTableCell(formatYoYChange(currentYearPredictedValues.tac75th, previousYearPredictedValues.tac75th))}
                    {renderTableCell(formatNumber(previousYearPredictedValues.tdc75th))}
                    {renderTableCell(formatNumber(currentYearPredictedValues.tdc75th))}
                    {renderTableCell(formatYoYChange(currentYearPredictedValues.tdc75th, previousYearPredictedValues.tdc75th))}
                </tr>
                <tr>
                    <td>Median</td>
                    {renderTableCell(formatNumber(previousYearPredictedValues.salaryMedian))}
                    {renderTableCell(formatNumber(currentYearPredictedValues.salaryMedian))}
                    {renderTableCell(formatYoYChange(currentYearPredictedValues.salaryMedian, previousYearPredictedValues.salaryMedian))}
                    {renderTableCell(formatNumber(previousYearPredictedValues.tacMedian))}
                    {renderTableCell(formatNumber(currentYearPredictedValues.tacMedian))}
                    {renderTableCell(formatYoYChange(currentYearPredictedValues.tacMedian, previousYearPredictedValues.tacMedian))}
                    {renderTableCell(formatNumber(previousYearPredictedValues.tdcMedian))}
                    {renderTableCell(formatNumber(currentYearPredictedValues.tdcMedian))}
                    {renderTableCell(formatYoYChange(currentYearPredictedValues.tdcMedian, previousYearPredictedValues.tdcMedian))}

                </tr>

                <tr>
                    <td>25th Percentile</td>
                    {renderTableCell(formatNumber(previousYearPredictedValues.salary25th))}
                    {renderTableCell(formatNumber(currentYearPredictedValues.salary25th))}
                    {renderTableCell(formatYoYChange(currentYearPredictedValues.salary25th, previousYearPredictedValues.salary25th))}
                    {renderTableCell(formatNumber(previousYearPredictedValues.tac25th))}
                    {renderTableCell(formatNumber(currentYearPredictedValues.tac25th))}
                    {renderTableCell(formatYoYChange(currentYearPredictedValues.tac25th, previousYearPredictedValues.tac25th))}
                    {renderTableCell(formatNumber(previousYearPredictedValues.tdc25th))}
                    {renderTableCell(formatNumber(currentYearPredictedValues.tdc25th))}
                    {renderTableCell(formatYoYChange(currentYearPredictedValues.tdc25th, previousYearPredictedValues.tdc25th))}
                </tr>
            </tbody>
        </table>
    )

    const handleCopyChartToClipboard = useCallback(async () => {
        const chartInstance = selectedChart === 'salary'
            ? salaryRegressionChartInstanceRef.current
            : selectedChart === 'tac'
                ? tacRegressionChartInstanceRef.current
                : tdcRegressionChartInstanceRef.current;

        if (!chartInstance?.canvas || !navigator.clipboard || typeof ClipboardItem === 'undefined') {
            setCopyChartStatus('idle');
            return;
        }

        try {
            setCopyChartStatus('copying');
            const blob = await new Promise((resolve) => {
                chartInstance.canvas.toBlob(resolve, 'image/png');
            });

            if (!blob) {
                throw new Error('Unable to create PNG');
            }

            await navigator.clipboard.write([
                new ClipboardItem({
                    'image/png': blob
                })
            ]);

            setCopyChartStatus('copied');
            window.setTimeout(() => {
                setCopyChartStatus('idle');
            }, 2000);
        } catch (copyError) {
            console.error('Error copying regression chart to clipboard:', copyError);
            setCopyChartStatus('idle');
        }
    }, [selectedChart]);

    const salaryRegressionChart = (
        <div className="regression-chart-panel">
            <div className="regression-chart-header-row">
                <div className="regression-chart-header">Base Salary Regression Curve</div>
                <Button
                    variant="outline-secondary"
                    size="sm"
                    className="copy-chart-button"
                    onClick={handleCopyChartToClipboard}
                    disabled={copyChartStatus === 'copying'}
                >
                    {copyChartStatus === 'copied' ? 'Copied' : copyChartStatus === 'copying' ? 'Copying...' : 'Copy Chart'}
                </Button>
            </div>
            <div className="regression-chart-canvas-wrapper">
                <canvas
                    ref={salaryRegressionChartCanvasRef}
                    aria-label="Base salary regression line chart"
                    role="img"
                />
            </div>
        </div>
    );

    const tacRegressionChart = (
        <div className="regression-chart-panel">
            <div className="regression-chart-header-row">
                <div className="regression-chart-header">Total Annual Compensation Regression Curve</div>
                <Button
                    variant="outline-secondary"
                    size="sm"
                    className="copy-chart-button"
                    onClick={handleCopyChartToClipboard}
                    disabled={copyChartStatus === 'copying'}
                >
                    {copyChartStatus === 'copied' ? 'Copied' : copyChartStatus === 'copying' ? 'Copying...' : 'Copy Chart'}
                </Button>
            </div>
            <div className="regression-chart-canvas-wrapper">
                <canvas
                    ref={tacRegressionChartCanvasRef}
                    aria-label="Total Annual Compensation regression line chart"
                    role="img"
                />
            </div>
        </div>
    );

    const tdcRegressionChart = (
        <div className="regression-chart-panel">
            <div className="regression-chart-header-row">
                <div className="regression-chart-header">Total Direct Compensation Regression Curve</div>
                <Button
                    variant="outline-secondary"
                    size="sm"
                    className="copy-chart-button"
                    onClick={handleCopyChartToClipboard}
                    disabled={copyChartStatus === 'copying'}
                >
                    {copyChartStatus === 'copied' ? 'Copied' : copyChartStatus === 'copying' ? 'Copying...' : 'Copy Chart'}
                </Button>
            </div>
            <div className="regression-chart-canvas-wrapper">
                <canvas
                    ref={tdcRegressionChartCanvasRef}
                    aria-label="Total Direct Compensation regression line chart"
                    role="img"
                />
            </div>
        </div>
    );

    const attachCursorHandlers = useCallback((chart) => {
        const canvas = chart?.canvas;
        const xScale = chart?.scales?.x;
        if (!canvas || !xScale) {
            return () => { };
        }

        const updateScopeFromEvent = (event) => {
            const rect = canvas.getBoundingClientRect();
            const pixelX = event.clientX - rect.left;
            const rawValue = xScale.getValueForPixel(pixelX);
            if (typeof rawValue !== 'number' || Number.isNaN(rawValue)) {
                return;
            }

            updateSelectedScope(rawValue);
        };

        const handlePointerDown = (event) => {
            const rect = canvas.getBoundingClientRect();
            const pixelX = event.clientX - rect.left;
            const cursorPixel = xScale.getPixelForValue(selectedScope);
            if (Math.abs(pixelX - cursorPixel) <= CURSOR_HANDLE_RADIUS * 2) {
                cursorDragStateRef.current.dragging = true;
                updateScopeFromEvent(event);
            }
        };

        const handlePointerMove = (event) => {
            if (!cursorDragStateRef.current.dragging) {
                return;
            }
            updateScopeFromEvent(event);
        };

        const handlePointerUp = () => {
            cursorDragStateRef.current.dragging = false;
        };

        const handleClick = (event) => {
            updateScopeFromEvent(event);
        };

        canvas.addEventListener('pointerdown', handlePointerDown);
        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);
        canvas.addEventListener('click', handleClick);

        return () => {
            canvas.removeEventListener('pointerdown', handlePointerDown);
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
            canvas.removeEventListener('click', handleClick);
            cursorDragStateRef.current.dragging = false;
        };
    }, [selectedScope, updateSelectedScope]);

    const buildRegressionChartOptions = useCallback((yAxisTitle, previousLabel, currentLabel, previousValue, currentValue) => ({
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        layout: {
            padding: {
                right: 150
            }
        },
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    usePointStyle: true,
                    boxWidth: 10,
                    padding: 20,
                    color: '#334155',
                    font: {
                        size: 24
                    }
                }
            },
            tooltip: {
                callbacks: {
                    title: (items) => {
                        const scopeValue = items[0]?.parsed?.x;
                        if (scopeValue === undefined) {
                            return '';
                        }

                        const scopeLabel = regressionType === 'revenue' ? 'Revenue' : 'Market Cap';
                        return `${scopeLabel}: ${Math.round(scopeValue).toLocaleString()}M`;
                    },
                    label: (context) => `${context.dataset.label}: ${Math.round(context.parsed.y).toLocaleString()}`
                }
            },
            scopeCursorPlugin: {
                xValue: selectedScope,
                cursorValues: [
                    { label: previousLabel, value: previousValue, color: '#e06500' },
                    { label: currentLabel, value: currentValue, color: '#00416A' }
                ]
            }
        },
        scales: {
            x: {
                type: 'linear',
                min: minScope,
                max: maxScope,
                title: {
                    display: true,
                    text: regressionType === 'revenue' ? 'Revenue ($M)' : 'Market Cap ($M)',
                    color: '#475569',
                    font: {
                        size: 27,
                        weight: '700'
                    }
                },
                ticks: {
                    color: '#64748b',
                    font: {
                        size: 22.5
                    },
                    callback: (value) => Number(value).toLocaleString()
                },
                grid: {
                    color: '#e2e8f0'
                },
                border: {
                    color: '#64748b',
                    width: 2
                }
            },
            y: {
                title: {
                    display: true,
                    text: yAxisTitle,
                    color: '#475569',
                    font: {
                        size: 27,
                        weight: '700'
                    }
                },
                ticks: {
                    color: '#64748b',
                    font: {
                        size: 22.5
                    },
                    callback: (value) => Number(value).toLocaleString()
                },
                grid: {
                    color: '#e2e8f0'
                },
                border: {
                    color: '#64748b',
                    width: 2
                }
            }
        }
    }), [maxScope, minScope, regressionType, selectedScope]);

    const checkDataAccess = async () => {
        const user = await Auth.currentAuthenticatedUser();
        let userGroups = user.signInUserSession.accessToken.payload["cognito:groups"];
        return userGroups?.some(group => ['FWC_Admin', 'Internal', 'External', 'Client'].includes(group));
    }

    const handleRetrieveRegressionData = useCallback(async () => {

        const dataAccess = await checkDataAccess();
        if (!dataAccess) {
            setError("You do not have access to view this data. Please reachout to data@fwcook.com to request access.");
            setDisplayError(true);
            return;
        }

        const token = await getAuthToken();
        const years = `${surveyYear},${previousSurveyYear}`;
        const queryStringParameters = {
            years,
            benchmark: selectedBenchmark,
            industryGrouping: selectedIndustryGrouping,
            reportingLevelGrouping: selectedReportingLevelGrouping
        };
        setDisplayError(false);
        setError(null);

        API.get('AnnualCompensations', `/survey-output/regression/${regressionType}`, {
            headers: {
                'Content-Type': 'application/json',
                Token: token,
            },
            queryStringParameters
        }).then((response) => {
            handleSetData(response);
        }).catch((error) => {
            console.error("Error retrieving regression data:", error);
            setError("Error retrieving regression data");
            setDisplayError(true);
        });

    }, [handleSetData, selectedBenchmark, selectedIndustryGrouping, selectedReportingLevelGrouping, regressionType]);

    useEffect(() => {
        const mounted = true;
        if (mounted) {
            setSelectedBenchmark("Chief Executive Officer");
            setSelectedIndustryGrouping("General Industry")
            setSelectedReportingLevelGrouping("All Reporting Levels")
        }
    }, [regressionType]);

    useEffect(() => {
        const mounted = true;
        if (mounted) {
            const scopeConfig = getScopeConfig(regressionType);
            setPreviousYearData({});
            setCurrentYearData({});
            setError(null);
            setDisplayError(false);
            setSelectedScope(scopeConfig.selected);
            setScopeInputValue(formatScopeValue(scopeConfig.selected));
            setSelectedChart("tdc");
            setMinScope(scopeConfig.minimum);
            setMaxScope(scopeConfig.maximum);
            setScopeUpperLimit(scopeConfig.upperLimit);
            setMinScopeInputValue(formatScopeValue(scopeConfig.minimum));
            setMaxScopeInputValue(formatScopeValue(scopeConfig.maximum));
            setMinScopeError("");
            setMaxScopeError("");
            handleRetrieveRegressionData();
        }
    }, [handleRetrieveRegressionData, regressionType]);


    useEffect(() => {
        if (maxScope <= 0 || minScope > maxScope) {
            return;
        }

        const clampedScope = Math.round(clamp(selectedScope, minScope, maxScope));
        if (clampedScope !== selectedScope) {
            setSelectedScope(clampedScope);
            return;
        }

        setScopeInputValue(formatScopeValue(clampedScope));
    }, [maxScope, minScope, selectedScope]);

    useEffect(() => {
        if (selectedChart !== 'salary') {
            if (salaryRegressionChartInstanceRef.current) {
                salaryRegressionChartInstanceRef.current.destroy();
                salaryRegressionChartInstanceRef.current = null;
            }
            return undefined;
        }

        const canvas = salaryRegressionChartCanvasRef.current;
        if (!canvas) {
            return undefined;
        }

        if (salaryRegressionChartInstanceRef.current) {
            salaryRegressionChartInstanceRef.current.destroy();
        }

        salaryRegressionChartInstanceRef.current = new Chart(canvas, {
            type: 'line',
            plugins: [scopeCursorPlugin, curveEndLabelPlugin, legendSpacingPlugin],
            data: {
                datasets: [
                    {
                        label: ` ${previousSurveyYear} Base Salary `,
                        data: previousYearSalaryRegressionSeries,
                        borderColor: '#e06500',
                        backgroundColor: '#e06500',
                        rSquared: previousYearData.baseSalaryRSquared,
                        rSquaredLabel: `${previousSurveyYear} R²`,
                        borderWidth: 3,
                        pointRadius: 0,
                        tension: 0
                    },
                    {
                        label: ` ${surveyYear} Base Salary `,
                        data: currentYearSalaryRegressionSeries,
                        borderColor: '#00416A',
                        backgroundColor: '#00416A',
                        rSquared: currentYearData.baseSalaryRSquared,
                        rSquaredLabel: `${surveyYear} R²`,
                        borderWidth: 3,
                        pointRadius: 0,
                        tension: 0
                    }
                ]
            },
            options: buildRegressionChartOptions(
                'Base Salary ($000)',
                `${previousSurveyYear} Base Salary`,
                `${surveyYear} Base Salary`,
                previousYearSelectedPredictedValues.salary,
                currentYearSelectedPredictedValues.salary
            )
        });

        const detachCursorHandlers = attachCursorHandlers(salaryRegressionChartInstanceRef.current);

        return () => {
            detachCursorHandlers();
            if (salaryRegressionChartInstanceRef.current) {
                salaryRegressionChartInstanceRef.current.destroy();
                salaryRegressionChartInstanceRef.current = null;
            }
        };
    }, [
        attachCursorHandlers,
        buildRegressionChartOptions,
        currentYearSelectedPredictedValues.salary,
        currentYearSalaryRegressionSeries,
        previousYearSelectedPredictedValues.salary,
        previousYearData.baseSalaryRSquared,
        previousYearSalaryRegressionSeries,
        currentYearData.baseSalaryRSquared,
        selectedChart
    ]);

    useEffect(() => {
        if (selectedChart !== 'tac') {
            if (tacRegressionChartInstanceRef.current) {
                tacRegressionChartInstanceRef.current.destroy();
                tacRegressionChartInstanceRef.current = null;
            }
            return undefined;
        }

        const canvas = tacRegressionChartCanvasRef.current;
        if (!canvas) {
            return undefined;
        }

        if (tacRegressionChartInstanceRef.current) {
            tacRegressionChartInstanceRef.current.destroy();
        }

        tacRegressionChartInstanceRef.current = new Chart(canvas, {
            type: 'line',
            plugins: [scopeCursorPlugin, curveEndLabelPlugin, legendSpacingPlugin],
            data: {
                datasets: [
                    {
                        label: ` ${previousSurveyYear} TAC `,
                        data: previousYearTacRegressionSeries,
                        borderColor: '#e06500',
                        backgroundColor: '#e06500',
                        rSquared: previousYearData.tacRSquared,
                        rSquaredLabel: `${previousSurveyYear} R²`,
                        borderWidth: 3,
                        pointRadius: 0,
                        tension: 0
                    },
                    {
                        label: ` ${surveyYear} TAC `,
                        data: currentYearTacRegressionSeries,
                        borderColor: '#00416A',
                        backgroundColor: '#00416A',
                        rSquared: currentYearData.tacRSquared,
                        rSquaredLabel: `${surveyYear} R²`,
                        borderWidth: 3,
                        pointRadius: 0,
                        tension: 0
                    }
                ]
            },
            options: buildRegressionChartOptions(
                'Total Annual Compensation ($000)',
                `${previousSurveyYear} TAC`,
                `${surveyYear} TAC`,
                previousYearSelectedPredictedValues.tac,
                currentYearSelectedPredictedValues.tac
            )
        });

        const detachCursorHandlers = attachCursorHandlers(tacRegressionChartInstanceRef.current);

        return () => {
            detachCursorHandlers();
            if (tacRegressionChartInstanceRef.current) {
                tacRegressionChartInstanceRef.current.destroy();
                tacRegressionChartInstanceRef.current = null;
            }
        };
    }, [
        attachCursorHandlers,
        buildRegressionChartOptions,
        currentYearSelectedPredictedValues.tac,
        currentYearTacRegressionSeries,
        previousYearSelectedPredictedValues.tac,
        previousYearData.tacRSquared,
        previousYearTacRegressionSeries,
        currentYearData.tacRSquared,
        selectedChart
    ]);

    useEffect(() => {
        if (selectedChart !== 'tdc') {
            if (tdcRegressionChartInstanceRef.current) {
                tdcRegressionChartInstanceRef.current.destroy();
                tdcRegressionChartInstanceRef.current = null;
            }
            return undefined;
        }

        const canvas = tdcRegressionChartCanvasRef.current;
        if (!canvas) {
            return undefined;
        }

        if (tdcRegressionChartInstanceRef.current) {
            tdcRegressionChartInstanceRef.current.destroy();
        }

        tdcRegressionChartInstanceRef.current = new Chart(canvas, {
            type: 'line',
            plugins: [scopeCursorPlugin, curveEndLabelPlugin, legendSpacingPlugin],
            data: {
                datasets: [
                    {
                        label: ` ${previousSurveyYear} TDC `,
                        data: previousYearTdcRegressionSeries,
                        borderColor: '#e06500',
                        backgroundColor: '#e06500',
                        rSquared: previousYearData.tdcRSquared,
                        rSquaredLabel: `${previousSurveyYear} R²`,
                        borderWidth: 3,
                        pointRadius: 0,
                        tension: 0
                    },
                    {
                        label: ` ${surveyYear} TDC `,
                        data: currentYearTdcRegressionSeries,
                        borderColor: '#00416A',
                        backgroundColor: '#00416A',
                        rSquared: currentYearData.tdcRSquared,
                        rSquaredLabel: `${surveyYear} R²`,
                        borderWidth: 3,
                        pointRadius: 0,
                        tension: 0
                    }
                ]
            },
            options: buildRegressionChartOptions(
                'Total Direct Compensation ($000)',
                `${previousSurveyYear} TDC`,
                `${surveyYear} TDC`,
                previousYearSelectedPredictedValues.tdc,
                currentYearSelectedPredictedValues.tdc
            )
        });

        const detachCursorHandlers = attachCursorHandlers(tdcRegressionChartInstanceRef.current);

        return () => {
            detachCursorHandlers();
            if (tdcRegressionChartInstanceRef.current) {
                tdcRegressionChartInstanceRef.current.destroy();
                tdcRegressionChartInstanceRef.current = null;
            }
        };
    }, [
        attachCursorHandlers,
        buildRegressionChartOptions,
        currentYearSelectedPredictedValues.tdc,
        currentYearTdcRegressionSeries,
        previousYearSelectedPredictedValues.tdc,
        previousYearData.tdcRSquared,
        previousYearTdcRegressionSeries,
        currentYearData.tdcRSquared,
        selectedChart
    ]);

    return (
        <div className="survey-regression-dashboard-page">
            <Card className="survey-regression-dashboard-console" variant="outlined">
                <Card.Header className="survey-dashboard-header">
                    <div>
                        <div className="survey-dashboard-kicker">EXECUTIVE COMPENSATION INTELLIGENCE</div>
                        <h1>{regressionType === "revenue" ? "Revenue Regression" : "Market Cap Regression"}</h1>
                    </div>
                    <div className="survey-dashboard-context"><span>DATA COVERAGE</span>{previousSurveyYear}–{surveyYear} surveys</div>
                </Card.Header>
                <Card.Body>
                    <p className="text-muted">Use the filters below to retrieve regression data from the {surveyYear} and {previousSurveyYear} surveys. Not every benchmark for every industry and reporting level will be available.</p>
                    <div className="filter-panel">
                        <Form.Group className="filter-group">
                            <Form.Label>Role Code</Form.Label>
                            <Form.Control as="select" value={selectedBenchmark} onChange={(e) => setSelectedBenchmark(e.target.value)}>
                                {Object.values(BENCHMARK_CODE_MAP).map((benchmark) => (
                                    <option key={benchmark} value={benchmark}>{benchmark}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>

                        <Form.Group className="filter-group">
                            <Form.Label>Industry Grouping</Form.Label>
                            <Form.Control as="select" value={selectedIndustryGrouping} onChange={(e) => setSelectedIndustryGrouping(e.target.value)}>
                                {regressionType === 'revenue' ? Object.values(REVENUE_INDUSTRY_GROUPINGS).map((grouping) => (
                                    <option key={grouping} value={grouping}>{grouping}</option>
                                )) : Object.values(MARKET_CAP_INDUSTRY_GROUPINGS).map((grouping) => (
                                    <option key={grouping} value={grouping}>{grouping}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                        <Form.Group className="filter-group">
                            <Form.Label>Reporting Level Grouping</Form.Label>
                            <Form.Control as="select" value={selectedReportingLevelGrouping} onChange={(e) => setSelectedReportingLevelGrouping(e.target.value)}>
                                {Object.values(REPORTING_LEVEL_GROUPINGS).map(grouping => (
                                    <option key={grouping} value={grouping}>{grouping}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                        <Form.Group className="filter-group">
                            <Form.Label>Displayed Chart</Form.Label>
                            <Form.Control as="select" value={selectedChart} onChange={(e) => setSelectedChart(e.target.value)}>
                                <option key={"tdc"} value={"tdc"}>Total Direct Compensation</option>
                                <option key={"tac"} value={"tac"}>Total Annual Compensation</option>
                                <option key={"salary"} value={"salary"}>Base Salary</option>
                            </Form.Control>
                        </Form.Group>
                        <Form.Group className="filter-group percentile-filter-group">
                            <Form.Label>Percentile</Form.Label>
                            <Form.Control
                                as="select"
                                value={selectedPercentile}
                                onChange={(e) => setSelectedPercentile(e.target.value)}
                            >
                                <option value="75th">75th Percentile</option>
                                <option value="median">Median</option>
                                <option value="25th">25th Percentile</option>
                            </Form.Control>
                        </Form.Group>
                        <div className="scope-bounds-group">
                            <Form.Group className="filter-group scope-bound-group">
                                <Form.Label htmlFor="minimum-scope-input">Min Scope ($Mil)</Form.Label>
                                <Form.Control
                                    id="minimum-scope-input"
                                    type="text"
                                    inputMode="numeric"
                                    value={minScopeInputValue}
                                    isInvalid={Boolean(minScopeError)}
                                    aria-label={`Minimum ${regressionType === "revenue" ? "revenue" : "market cap"} scope in millions`}
                                    onChange={(e) => {
                                        setMinScopeInputValue(e.target.value.replace(/[^\d,]/g, ''));
                                        setMinScopeError("");
                                    }}
                                    onBlur={commitMinScopeInputValue}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            commitMinScopeInputValue();
                                        }
                                    }}
                                />
                                <Form.Control.Feedback type="invalid">{minScopeError}</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="filter-group scope-bound-group">
                                <Form.Label htmlFor="maximum-scope-input">Max Scope ($Mil)</Form.Label>
                                <Form.Control
                                    id="maximum-scope-input"
                                    type="text"
                                    inputMode="numeric"
                                    value={maxScopeInputValue}
                                    isInvalid={Boolean(maxScopeError)}
                                    aria-label={`Maximum ${regressionType === "revenue" ? "revenue" : "market cap"} scope in millions`}
                                    onChange={(e) => {
                                        setMaxScopeInputValue(e.target.value.replace(/[^\d,]/g, ''));
                                        setMaxScopeError("");
                                    }}
                                    onBlur={commitMaxScopeInputValue}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            commitMaxScopeInputValue();
                                        }
                                    }}
                                />
                                <Form.Control.Feedback type="invalid">{maxScopeError}</Form.Control.Feedback>
                            </Form.Group>
                        </div>
                        <div className="filter-group scope-filter-group">
                            {maxScope > 0 && <div className='scope-range'>
                                <Form.Label htmlFor="range-input">
                                    {regressionType === "revenue" ? "Revenue" : "Market Cap"} Scope ($Mil)
                                </Form.Label>
                                <div className="scope-range-controls">
                                    <Form.Control
                                        type="text"
                                        value={scopeInputValue}
                                        inputMode="numeric"
                                        className="scope-range-number-input"
                                        aria-label={`${regressionType === "revenue" ? "Revenue" : "Market Cap"} scope value in millions`}
                                        onChange={(e) => setScopeInputValue(e.target.value.replace(/[^\d,]/g, ''))}
                                        onBlur={commitScopeInputValue}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                commitScopeInputValue();
                                            }
                                        }}
                                    />
                                    <input
                                        id="range-input"
                                        type="range"
                                        min={minScope}
                                        max={maxScope}
                                        value={selectedScope}
                                        onChange={(e) => updateSelectedScope(Number(e.target.value))}
                                        className="scope-range-input"
                                    />
                                </div>
                            </div>}
                        </div>
                    </div>
                    {displayError && error && (
                        <Alert variant="danger" className="mt-3">
                            {error}
                        </Alert>
                    )}
                    <div className="data-chart">
                        {selectedChart === "tdc" ? tdcRegressionChart : null}
                        {selectedChart === "tac" ? tacRegressionChart : null}
                        {selectedChart === "salary" ? salaryRegressionChart : null}
                    </div>
                    <div className="data-panel">
                        <div className="data-table-wrapper">
                            {dataTable}
                        </div>
                    </div>
                </Card.Body>
            </Card>
        </div >
    )
}

export default SurveyRegressionDashboardConsole;
